import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, mergeMap } from 'rxjs';

import { PlanFindingsActions } from '@core/store/plan-findings/plan-findings.actions';
import { selectPlanFindingsState } from '@core/store/plan-findings/plan-findings.selectors';
import * as PlanActions from '@core/store/plan/plan.actions';
import { selectPlan } from '@core/store/plan/plan.selectors';

import { PlanEditActions } from './plan-edit.actions';
import { selectPlanEditLastRevision } from './plan-edit.selectors';

@Injectable()
export class PlanEditEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  /**
   * One save reaches the saver's tab twice, from the assistant's event and from GBE's `plan:updated`;
   * the revision makes the second a no-op.
   */
  readonly refreshAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanEditActions.planSaved, PlanEditActions.planUpdatedRemotely),
      concatLatestFrom(() => [
        this.store.select(selectPlanEditLastRevision),
        this.store.select(selectPlan),
        this.store.select(selectPlanFindingsState)
      ]),
      filter(([{ planId, planDataRevision }, last]) => last[planId] !== planDataRevision),
      mergeMap(([{ planId, planDataRevision }, , plan, findings]) => {
        const inView = plan?.id === planId;
        return [
          ...(inView
            ? [
                PlanActions.fetchPlans({ id: planId }),
                PlanEditActions.planDataReplaced({ planId })
              ]
            : []),
          ...(inView && findings.planId === planId && findings.paramId !== null
            ? [PlanFindingsActions.readRequested({ planId, paramId: findings.paramId })]
            : []),
          PlanEditActions.planRefreshed({ planId, planDataRevision })
        ];
      })
    )
  );
}
